/**
 * Currency minor-unit exponent registry and conversion utilities.
 *
 * The standard ISO 4217 accounting exponent for most currencies is 2 (÷100),
 * so only the non-standard cases are listed here. Currencies absent from both
 * sets default to exponent 2.
 */

/**
 * Zero-decimal currencies (ISO 4217 exponent = 0).
 * Amounts for these currencies are already in major units — no division needed.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
    'BIF', // Burundian Franc
    'CLP', // Chilean Peso
    'DJF', // Djiboutian Franc
    'GNF', // Guinean Franc
    'ISK', // Icelandic Króna
    'JPY', // Japanese Yen
    'KMF', // Comorian Franc
    'KRW', // South Korean Won
    'MGA', // Malagasy Ariary
    'PYG', // Paraguayan Guaraní
    'RWF', // Rwandan Franc
    'UGX', // Ugandan Shilling
    'UYI', // Uruguay Peso en Unidades Indexadas
    'VND', // Vietnamese Đồng
    'VUV', // Vanuatu Vatu
    'XAF', // Central African CFA Franc
    'XOF', // West African CFA Franc
    'XPF', // CFP Franc
])

/**
 * Three-decimal currencies (ISO 4217 exponent = 3).
 * Minor-unit amounts for these currencies must be divided by 1000.
 */
const THREE_DECIMAL_CURRENCIES = new Set([
    'BHD', // Bahraini Dinar
    'IQD', // Iraqi Dinar
    'JOD', // Jordanian Dinar
    'KWD', // Kuwaiti Dinar
    'LYD', // Libyan Dinar
    'OMR', // Omani Rial
    'TND', // Tunisian Dinar
])

/**
 * Returns the ISO 4217 accounting exponent for the given currency code.
 * Falls back to 2 for any unrecognised currency (standard convention).
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {number} The minor-unit exponent (0, 2, or 3)
 */
export function getCurrencyExponent(currencyCode) {
    if (!currencyCode || typeof currencyCode !== 'string') {
        return 2
    }
    const code = currencyCode.toUpperCase()
    if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0
    if (THREE_DECIMAL_CURRENCIES.has(code)) return 3
    return 2
}

/**
 * Converts a minor-unit integer amount to its major-unit equivalent using the
 * currency's ISO 4217 exponent.
 *
 * Falls back to exponent 2 (÷100 convention) when the currency is unknown,
 * so unrecognised currencies behave like standard two-decimal currencies
 * rather than failing silently.
 *
 * Examples:
 *   fromMinorUnits(100051, 'THB') → 1000.51  (exponent 2, ÷100)
 *   fromMinorUnits(1000,   'JPY') → 1000     (exponent 0, ÷1)
 *   fromMinorUnits(100000, 'INR') → 1000     (exponent 2, ÷100)
 *   fromMinorUnits(100000, 'KWD') → 100      (exponent 3, ÷1000)
 *   fromMinorUnits(100000, null)  → 1000     (fallback exponent 2)
 *
 * @param {number} minorAmount - Amount in minor units
 * @param {string|null|undefined} currencyCode - ISO 4217 currency code
 * @returns {number} Amount in major units
 */
export function fromMinorUnits(minorAmount, currencyCode) {
    if (minorAmount == null || typeof minorAmount !== 'number') {
        return minorAmount
    }
    const exponent = getCurrencyExponent(currencyCode)
    if (exponent === 0) {
        return minorAmount
    }
    const divisor = Math.pow(10, exponent)
    return minorAmount / divisor
}
